import React, { useState, useEffect, useCallback } from 'react';

const LABELS = {
  generalLimiterMax:  { label: 'Limite geral de requisições',     unit: 'req / 15 min',  min: 100,  max: 10000, desc: 'Máximo de requisições aceitas por IP a cada 15 minutos (todas as rotas).' },
  authLimiterMax:     { label: 'Limite de tentativas de login',    unit: 'req / 15 min',  min: 10,   max: 2000,  desc: 'Proteção anti brute-force nas rotas /auth/google e /auth/local.' },
  localCacheTtlMs:    { label: 'TTL do cache de usuários locais',  unit: 'ms',            min: 10000,max: 1800000,desc: 'Tempo em ms que o perfil de usuário local fica em memória sem consultar o banco.' },
  tokenCacheSlack:    { label: 'Antecipação de expiração do token Google', unit: 's',     min: 0,    max: 300,   desc: 'Segundos antes do vencimento do JWT Google em que o cache é invalidado preventivamente.' },
  googleCacheMax:     { label: 'Máx. tokens Google em cache',      unit: 'entradas',      min: 50,   max: 5000,  desc: 'Número máximo de tokens Google verificados mantidos em memória.' },
  localCacheMax:      { label: 'Máx. usuários locais em cache',    unit: 'entradas',      min: 10,   max: 2000,  desc: 'Número máximo de perfis de usuários locais mantidos em memória.' },
};

const GRUPOS = [
  {
    titulo: 'Rate Limiting',
    icon: '🚦',
    desc: 'Controla quantas requisições são aceitas por IP em 15 minutos. Valores muito baixos podem bloquear usuários legítimos; valores muito altos reduzem a proteção contra abusos.',
    campos: ['generalLimiterMax', 'authLimiterMax'],
  },
  {
    titulo: 'Cache de Autenticação',
    icon: '🗄️',
    desc: 'Tokens verificados ficam em memória para evitar chamadas repetidas ao Google e ao banco de dados. Ajuste o tamanho e o TTL conforme o número de usuários ativos.',
    campos: ['localCacheTtlMs', 'tokenCacheSlack', 'googleCacheMax', 'localCacheMax'],
  },
];

function CampoConfig({ chave, valor, onChange, dirty }) {
  const meta = LABELS[chave];
  if (!meta) return null;

  const displayVal = chave === 'localCacheTtlMs'
    ? `${Math.round(valor / 1000)}s  (${(valor / 60000).toFixed(1)} min)`
    : null;

  return (
    <div style={{
      padding: '16px',
      borderRadius: '10px',
      border: dirty ? '1.5px solid #3b82f6' : '1px solid #e2e8f0',
      backgroundColor: dirty ? '#eff6ff' : '#f8fafc',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.9rem', marginBottom: 2 }}>
            {meta.label}
            {dirty && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: '#3b82f6', fontWeight: 700 }}>alterado</span>}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{meta.desc}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <input
            type="number"
            value={valor}
            min={meta.min}
            max={meta.max}
            onChange={e => onChange(chave, Number(e.target.value))}
            style={{
              width: 110,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1.5px solid #cbd5e1',
              fontSize: '0.9rem',
              fontWeight: 600,
              textAlign: 'right',
              outline: 'none',
              backgroundColor: 'white',
            }}
          />
          <span style={{ fontSize: '0.78rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{meta.unit}</span>
        </div>
      </div>
      {displayVal && (
        <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#3b82f6' }}>≈ {displayVal}</div>
      )}
    </div>
  );
}

// ======= ABA DE CONFIGURAÇÕES =======
function AbaConfiguracoes({ createApi, isMobile }) {
  const [config, setConfig]         = useState(null);
  const [defaults, setDefaults]     = useState(null);
  const [editado, setEditado]       = useState({});
  const [cacheStats, setCacheStats] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [limpando, setLimpando]     = useState(false);
  const [notificacao, setNotificacao] = useState(null);

  const showNotif = useCallback((tipo, mensagem) => {
    setNotificacao({ tipo, mensagem });
    setTimeout(() => setNotificacao(null), 4000);
  }, []);

  const carregarConfig = useCallback(async () => {
    setLoading(true);
    try {
      const api = createApi();
      const [cfgRes, statsRes] = await Promise.all([
        api.get('/admin/sistema/config'),
        api.get('/admin/sistema/cache-stats'),
      ]);
      setConfig(cfgRes.data.config);
      setDefaults(cfgRes.data.defaults);
      setEditado({ ...cfgRes.data.config });
      setCacheStats(statsRes.data);
    } catch (err) {
      showNotif('error', err.response?.data?.error || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, [createApi, showNotif]);

  useEffect(() => { carregarConfig(); }, [carregarConfig]);

  const handleChange = (chave, valor) => setEditado(prev => ({ ...prev, [chave]: valor }));
  const isDirty = (chave) => config && editado[chave] !== config[chave];
  const hasDirty = config && Object.keys(LABELS).some(k => editado[k] !== config[k]);

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const api = createApi();
      const res = await api.put('/admin/sistema/config', { config: editado });
      setConfig(res.data.config);
      setEditado({ ...res.data.config });
      showNotif('success', res.data.message || 'Configurações salvas!');
    } catch (err) {
      showNotif('error', err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleRestaurarPadrao = () => {
    if (!defaults) return;
    setEditado({ ...defaults });
  };

  const handleLimparCache = async () => {
    if (!window.confirm('Limpar todo o cache de autenticação? Todos os usuários precisarão revalidar na próxima requisição.')) return;
    setLimpando(true);
    try {
      const api = createApi();
      await api.post('/admin/sistema/limpar-cache');
      showNotif('success', 'Cache limpo com sucesso!');
      const statsRes = await api.get('/admin/sistema/cache-stats');
      setCacheStats(statsRes.data);
    } catch (err) {
      showNotif('error', err.response?.data?.error || 'Erro ao limpar cache');
    } finally {
      setLimpando(false);
    }
  };

  const corNotif = { success: '#10b981', error: '#ef4444', info: '#3b82f6' };

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando configurações…</div>
  );

  return (
    <div>
      {notificacao && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '14px 20px', borderRadius: 12, backgroundColor: 'white',
          borderLeft: `4px solid ${corNotif[notificacao.tipo] || '#3b82f6'}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontWeight: 500, fontSize: '0.9rem',
          display: 'flex', alignItems: 'center', gap: 10, maxWidth: 360,
        }}>
          <span>{notificacao.tipo === 'success' ? '✅' : notificacao.tipo === 'error' ? '❌' : 'ℹ️'}</span>
          {notificacao.mensagem}
        </div>
      )}

      {cacheStats && (
        <div style={{
          backgroundColor: 'white', borderRadius: 14, padding: '20px 24px',
          border: '1px solid #e2e8f0', marginBottom: 24,
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              🗄️ Status do Cache
            </h3>
            <button
              onClick={handleLimparCache}
              disabled={limpando}
              style={{
                padding: '6px 16px', borderRadius: 8, border: '1px solid #fca5a5',
                backgroundColor: limpando ? '#f1f5f9' : '#fff1f1',
                color: limpando ? '#94a3b8' : '#dc2626',
                fontWeight: 600, cursor: limpando ? 'not-allowed' : 'pointer',
                fontSize: '0.82rem', transition: 'all 0.15s',
              }}
            >
              {limpando ? 'Limpando…' : '🗑️ Limpar Cache'}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            {[
              {
                label: 'Sessões Google válidas',
                desc:  'JWTs ainda não expirados em cache — cada aba/dispositivo logado gera 1 entrada',
                value: cacheStats.googleTokensCached,
                max:   cacheStats.googleCacheMax,
                color: '#3b82f6'
              },
              {
                label: 'Total no cache Google',
                desc:  'Inclui tokens prestes a expirar; limpos automaticamente a cada 5 min',
                value: cacheStats.googleTokensTotal,
                max:   cacheStats.googleCacheMax,
                color: '#8b5cf6'
              },
              {
                label: 'Sessões locais (senha)',
                desc:  'Usuários com login por e-mail/senha em cache — TTL configurável',
                value: cacheStats.localUsersCached,
                max:   cacheStats.localCacheMax,
                color: '#10b981'
              },
            ].map(stat => (
              <div key={stat.label} style={{ padding: '14px 16px', borderRadius: 10, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 2 }}>{stat.label}</div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 8, lineHeight: 1.4 }}>{stat.desc}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color }}>
                  {stat.value}
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>/ {stat.max}</span>
                </div>
                <div style={{ height: 4, borderRadius: 2, backgroundColor: '#e2e8f0', marginTop: 8, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 2,
                    width: `${Math.min(100, Math.round((stat.value / stat.max) * 100))}%`,
                    backgroundColor: stat.color, transition: 'width 0.4s',
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editado && GRUPOS.map(grupo => (
        <div key={grupo.titulo} style={{
          backgroundColor: 'white', borderRadius: 14, padding: '20px 24px',
          border: '1px solid #e2e8f0', marginBottom: 20,
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
            {grupo.icon} {grupo.titulo}
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#64748b' }}>{grupo.desc}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {grupo.campos.map(chave => (
              <CampoConfig key={chave} chave={chave} valor={editado[chave] ?? 0} onChange={handleChange} dirty={isDirty(chave)} />
            ))}
          </div>
        </div>
      ))}

      {editado && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 0', flexWrap: 'wrap' }}>
          <button
            onClick={handleRestaurarPadrao}
            style={{ padding: '9px 20px', borderRadius: 9, border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
          >
            Restaurar padrões
          </button>
          <button
            onClick={handleSalvar}
            disabled={salvando || !hasDirty}
            style={{
              padding: '9px 24px', borderRadius: 9, border: 'none',
              backgroundColor: hasDirty && !salvando ? '#0f5a30' : '#94a3b8',
              color: 'white', fontWeight: 700, cursor: hasDirty && !salvando ? 'pointer' : 'not-allowed',
              fontSize: '0.88rem', transition: 'background-color 0.15s',
            }}
          >
            {salvando ? 'Salvando…' : '💾 Salvar configurações'}
          </button>
        </div>
      )}
    </div>
  );
}

// ======= ABA DE LOGS =======
function AbaLogs({ createApi, isMobile }) {
  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [page, setPage]           = useState(1);
  const [filtroResultado, setFiltroResultado] = useState('');
  const [filtroEmail, setFiltroEmail]         = useState('');
  const [filtroInicio, setFiltroInicio]       = useState('');
  const [filtroFim, setFiltroFim]             = useState('');
  const limit = 50;

  const buscar = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const api = createApi();
      const params = new URLSearchParams({ page: p, limit });
      if (filtroResultado) params.set('resultado', filtroResultado);
      if (filtroEmail)     params.set('email', filtroEmail);
      if (filtroInicio)    params.set('data_inicio', filtroInicio);
      if (filtroFim)       params.set('data_fim', filtroFim);
      const res = await api.get(`/admin/sistema/login-logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      console.error('Erro ao buscar logs:', err);
    } finally {
      setLoading(false);
    }
  }, [createApi, filtroResultado, filtroEmail, filtroInicio, filtroFim]);

  useEffect(() => { buscar(1); }, [buscar]);

  const totalPaginas = Math.ceil(total / limit);

  const fmtData = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const corResultado = (r) => r === 'sucesso' ? '#10b981' : '#ef4444';
  const bgResultado  = (r) => r === 'sucesso' ? '#f0fdf4' : '#fef2f2';
  const borderResultado = (r) => r === 'sucesso' ? '#bbf7d0' : '#fecaca';

  const sucesso = logs.filter(l => l.resultado === 'sucesso').length;
  const falha   = logs.filter(l => l.resultado === 'falha').length;

  return (
    <div>
      {/* Filtros */}
      <div style={{
        backgroundColor: 'white', borderRadius: 14, padding: '20px 24px',
        border: '1px solid #e2e8f0', marginBottom: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
          🔍 Filtros
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Resultado</label>
            <select
              value={filtroResultado}
              onChange={e => setFiltroResultado(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '0.88rem', outline: 'none' }}
            >
              <option value="">Todos</option>
              <option value="sucesso">Sucesso</option>
              <option value="falha">Falha</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>E-mail</label>
            <input
              type="text"
              placeholder="Buscar por e-mail…"
              value={filtroEmail}
              onChange={e => setFiltroEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscar(1)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Data início</label>
            <input
              type="date"
              value={filtroInicio}
              onChange={e => setFiltroInicio(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>Data fim</label>
            <input
              type="date"
              value={filtroFim}
              onChange={e => setFiltroFim(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            onClick={() => buscar(1)}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#0f5a30', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}
          >
            Buscar
          </button>
          <button
            onClick={() => { setFiltroResultado(''); setFiltroEmail(''); setFiltroInicio(''); setFiltroFim(''); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {/* Resumo da página */}
      {!loading && logs.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ padding: '10px 18px', borderRadius: 10, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '0.88rem', fontWeight: 600, color: '#10b981' }}>
            ✅ {sucesso} sucesso{sucesso !== 1 ? 's' : ''}
          </div>
          <div style={{ padding: '10px 18px', borderRadius: 10, backgroundColor: '#fef2f2', border: '1px solid #fecaca', fontSize: '0.88rem', fontWeight: 600, color: '#ef4444' }}>
            ❌ {falha} falha{falha !== 1 ? 's' : ''}
          </div>
          <div style={{ padding: '10px 18px', borderRadius: 10, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.88rem', color: '#64748b' }}>
            Total filtrado: {total}
          </div>
        </div>
      )}

      {/* Tabela */}
      <div style={{
        backgroundColor: 'white', borderRadius: 14,
        border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando logs…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Nenhum log encontrado.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Data / Hora', 'E-mail', 'Nome', 'Tipo', 'Resultado', 'Motivo', 'IP'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap', color: '#475569', fontSize: '0.8rem' }}>
                      {fmtData(log.criado_at)}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#0f172a', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.email || '—'}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.nome || '—'}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                        fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: log.auth_type === 'google' ? '#eff6ff' : '#f0fdf4',
                        color: log.auth_type === 'google' ? '#3b82f6' : '#16a34a',
                        border: `1px solid ${log.auth_type === 'google' ? '#bfdbfe' : '#bbf7d0'}`,
                      }}>
                        {log.auth_type === 'google' ? '🔵 Google' : '🔑 Local'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                        fontSize: '0.75rem', fontWeight: 700,
                        backgroundColor: bgResultado(log.resultado),
                        color: corResultado(log.resultado),
                        border: `1px solid ${borderResultado(log.resultado)}`,
                      }}>
                        {log.resultado === 'sucesso' ? '✅ Sucesso' : '❌ Falha'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', color: '#64748b', fontSize: '0.8rem' }}>
                      {log.motivo || '—'}
                    </td>
                    <td style={{ padding: '11px 16px', color: '#94a3b8', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {log.ip || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          <button
            onClick={() => buscar(page - 1)}
            disabled={page <= 1}
            style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #cbd5e1', backgroundColor: page <= 1 ? '#f1f5f9' : 'white', color: page <= 1 ? '#94a3b8' : '#0f172a', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
          >
            ← Anterior
          </button>
          <span style={{ fontSize: '0.85rem', color: '#64748b', padding: '0 8px' }}>
            Página {page} de {totalPaginas}
          </span>
          <button
            onClick={() => buscar(page + 1)}
            disabled={page >= totalPaginas}
            style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #cbd5e1', backgroundColor: page >= totalPaginas ? '#f1f5f9' : 'white', color: page >= totalPaginas ? '#94a3b8' : '#0f172a', cursor: page >= totalPaginas ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
}

// ======= ABA DE MONITOR =======
function AbaMonitor({ createApi }) {
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [ultimaAt, setUltimaAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const buscar = useCallback(async () => {
    try {
      const api = createApi();
      const res = await api.get('/admin/sistema/live-stats');
      setStats(res.data);
      setUltimaAt(new Date());
    } catch (err) {
      console.error('Erro ao buscar live-stats:', err);
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => {
    buscar();
    if (!autoRefresh) return;
    const id = setInterval(buscar, 10000);
    return () => clearInterval(id);
  }, [buscar, autoRefresh]);

  const fmtUptime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  const StatCard = ({ label, desc, value, max, color, suffix = '', warn }) => {
    const pct = max != null ? Math.min(100, Math.round((value / max) * 100)) : null;
    const barColor = warn && pct >= warn ? '#ef4444' : color;
    return (
      <div style={{
        padding: '16px', borderRadius: 12,
        backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
      }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 2 }}>{label}</div>
        {desc && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 8, lineHeight: 1.4 }}>{desc}</div>}
        <div style={{ fontSize: '1.6rem', fontWeight: 800, color: barColor, lineHeight: 1 }}>
          {value != null ? value : '—'}
          {max != null && <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>/ {max}{suffix}</span>}
          {max == null && suffix && <span style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 400, marginLeft: 4 }}>{suffix}</span>}
        </div>
        {pct != null && (
          <div style={{ height: 5, borderRadius: 3, backgroundColor: '#e2e8f0', marginTop: 10, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, backgroundColor: barColor, transition: 'width 0.4s' }} />
          </div>
        )}
        {pct != null && (
          <div style={{ marginTop: 4, fontSize: '0.7rem', color: barColor, fontWeight: 600 }}>{pct}% do limite</div>
        )}
      </div>
    );
  };

  const HitRateCard = ({ label, hits, misses, color }) => {
    const total = hits + misses;
    const rate  = total > 0 ? Math.round((hits / total) * 100) : null;
    return (
      <div style={{ padding: '16px', borderRadius: 12, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: '1.6rem', fontWeight: 800, color: rate == null ? '#94a3b8' : rate >= 80 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444' }}>
          {rate != null ? `${rate}%` : 'sem dados'}
        </div>
        <div style={{ height: 5, borderRadius: 3, backgroundColor: '#e2e8f0', marginTop: 8, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, width: `${rate ?? 0}%`, backgroundColor: rate == null ? '#e2e8f0' : rate >= 80 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444', transition: 'width 0.4s' }} />
        </div>
        <div style={{ marginTop: 6, display: 'flex', gap: 12, fontSize: '0.72rem', color: '#64748b' }}>
          <span>✅ {hits} hits</span>
          <span>❌ {misses} misses</span>
          <span>Total: {total}</span>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando…</div>;

  return (
    <div>
      {/* Controles */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            backgroundColor: autoRefresh ? '#10b981' : '#94a3b8',
            boxShadow: autoRefresh ? '0 0 0 3px #d1fae5' : 'none',
          }} />
          <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
            {autoRefresh ? 'Atualiza a cada 10s' : 'Auto-refresh pausado'}
          </span>
          {ultimaAt && (
            <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
              · última: {ultimaAt.toLocaleTimeString('pt-BR')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setAutoRefresh(v => !v)}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}
          >
            {autoRefresh ? '⏸ Pausar' : '▶ Retomar'}
          </button>
          <button
            onClick={buscar}
            style={{ padding: '6px 14px', borderRadius: 8, border: 'none', backgroundColor: '#0f5a30', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}
          >
            🔄 Atualizar
          </button>
        </div>
      </div>

      {stats && (
        <>
          {/* Uptime */}
          <div style={{
            backgroundColor: 'white', borderRadius: 14, padding: '14px 20px',
            border: '1px solid #e2e8f0', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              🕐 <strong>Uptime do servidor:</strong> {fmtUptime(stats.uptime)}
            </span>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              📊 <strong>Janela atual:</strong> {stats.windowPct}% decorrida ({Math.round(stats.elapsedMs / 60000)}min de 15min)
            </span>
          </div>

          {/* Rate Limiting */}
          <div style={{
            backgroundColor: 'white', borderRadius: 14, padding: '20px 24px',
            border: '1px solid #e2e8f0', marginBottom: 20,
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              🚦 Rate Limiting — janela de 15 min
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#64748b' }}>
              Contadores acumulados desde o último restart do servidor.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <StatCard
                label="Requisições gerais"
                desc="Total aceitas desde o restart (todas as rotas, exceto /health)"
                value={stats.requests}
                max={stats.generalLimiterMax}
                color="#3b82f6"
                warn={80}
              />
              <StatCard
                label="Requisições bloqueadas"
                desc="Respondidas com HTTP 429 pelo limiter geral"
                value={stats.blocked}
                color="#ef4444"
              />
              <StatCard
                label="Tentativas de login"
                desc="Requisições nas rotas /auth/google e /auth/local"
                value={stats.authAttempts}
                max={stats.authLimiterMax}
                color="#f59e0b"
                warn={80}
              />
              <StatCard
                label="Logins bloqueados"
                desc="Tentativas de login recusadas por excesso (HTTP 429)"
                value={stats.authBlocked}
                color="#ef4444"
              />
            </div>
          </div>

          {/* Cache — hit rate */}
          <div style={{
            backgroundColor: 'white', borderRadius: 14, padding: '20px 24px',
            border: '1px solid #e2e8f0', marginBottom: 20,
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              🗄️ Cache de Autenticação — Taxa de Acerto
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#64748b' }}>
              Hit rate acima de 80% é considerado saudável. Valores baixos indicam TTL curto ou cache subdimensionado.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
              <HitRateCard
                label="Token Google — hit rate"
                hits={stats.cache.googleHits}
                misses={stats.cache.googleMisses}
                color="#3b82f6"
              />
              <HitRateCard
                label="Usuários locais — hit rate"
                hits={stats.cache.localHits}
                misses={stats.cache.localMisses}
                color="#10b981"
              />
            </div>
          </div>

          {/* Cache — ocupação */}
          <div style={{
            backgroundColor: 'white', borderRadius: 14, padding: '20px 24px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              📦 Ocupação do Cache
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: '#64748b' }}>
              Número de entradas atualmente em memória. Acima de 80% considere aumentar o limite nas configurações.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <StatCard
                label="Sessões Google válidas"
                desc="JWTs não expirados — cada dispositivo logado gera 1 entrada"
                value={stats.cache.googleCached}
                max={stats.cache.googleCacheMax}
                color="#3b82f6"
                warn={80}
              />
              <StatCard
                label="Total no cache Google"
                desc="Inclui tokens prestes a expirar; limpos automaticamente a cada 5 min"
                value={stats.cache.googleTotal}
                max={stats.cache.googleCacheMax}
                color="#8b5cf6"
                warn={90}
              />
              <StatCard
                label="Sessões locais (senha)"
                desc="Usuários com login por e-mail/senha em cache"
                value={stats.cache.localCached}
                max={stats.cache.localCacheMax}
                color="#10b981"
                warn={80}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ======= COMPONENTE PRINCIPAL =======
export const GerenciadorSistema = ({ createApi, isMobile = false }) => {
  const [abaAtiva, setAbaAtiva] = useState('configuracoes');

  const abas = [
    { id: 'monitor',       label: '📊 Monitor' },
    { id: 'configuracoes', label: '⚙️ Configurações' },
    { id: 'logs',          label: '📋 Logs de Acesso' },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px 0' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
          ⚙️ Sistema
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.88rem', marginTop: 6 }}>
          Configurações de segurança, cache e monitoramento de acessos.
        </p>
      </div>

      {/* Sub-abas */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 24,
        borderBottom: '2px solid #e2e8f0', paddingBottom: 0,
      }}>
        {abas.map(aba => (
          <button
            key={aba.id}
            onClick={() => setAbaAtiva(aba.id)}
            style={{
              padding: '9px 20px',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: abaAtiva === aba.id ? '#0f5a30' : 'transparent',
              color: abaAtiva === aba.id ? 'white' : '#64748b',
              fontWeight: abaAtiva === aba.id ? 700 : 500,
              cursor: 'pointer',
              fontSize: '0.88rem',
              transition: 'all 0.15s',
              marginBottom: abaAtiva === aba.id ? -2 : 0,
              borderBottom: abaAtiva === aba.id ? '2px solid #0f5a30' : '2px solid transparent',
            }}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {abaAtiva === 'monitor'        && <AbaMonitor       createApi={createApi} />}
      {abaAtiva === 'configuracoes' && <AbaConfiguracoes createApi={createApi} isMobile={isMobile} />}
      {abaAtiva === 'logs'          && <AbaLogs          createApi={createApi} isMobile={isMobile} />}
    </div>
  );
};
